import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRevenuePlays, type WinbackTarget } from "@/hooks/useRevenuePlays";
import { exportToCsv, stampedFilename } from "@/lib/exportCsv";
import { useTableSort } from "@/hooks/useTableSort";
import { SortableHead } from "@/components/SortableHead";
import { MetricCard } from "@/components/MetricCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  RefreshCw,
  Download,
  UserMinus,
  Crown,
  HeartPulse,
  TrendingUp,
  Snowflake,
  AlertTriangle,
  type LucideIcon,
} from "lucide-react";

const money = (v: unknown) =>
  `$${Number(v ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const pct = (v: unknown) => (v == null ? "—" : `${Number(v).toFixed(1)}%`);
const monthLabel = (iso: string) =>
  new Date(iso + (iso.length === 10 ? "T00:00:00" : "")).toLocaleDateString(undefined, {
    month: "short",
    year: "numeric",
  });
// Human "Last ordered" date. Null-safe (never renders "Invalid Date") and
// timezone-safe for date-only strings (pin to local midnight, not UTC).
const orderedDate = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

// Escalating severity: cooling (mild) -> at-risk -> lapsing (critical).
// Badge tints follow the DESIGN.md status ladder: caution -> warning -> danger.
const SEGMENT_STYLES: Record<string, string> = {
  cooling: "bg-status-caution/20 text-status-caution-foreground hover:bg-status-caution/20",
  at_risk: "bg-status-warning/15 text-status-warning hover:bg-status-warning/15",
  lapsing: "bg-status-danger/15 text-status-danger hover:bg-status-danger/15",
};

// Per-tier presentation. `level` (1..3) drives the color-independent severity
// ramp so the escalation reads without relying on hue alone; `Icon` reinforces
// meaning (cold -> warning -> critical). `fill` / `text` are DESIGN tokens.
type Tone = "caution" | "warning" | "danger";
const TONE: Record<Tone, { fill: string; text: string; iconBg: string }> = {
  caution: { fill: "bg-status-caution", text: "text-status-caution-foreground", iconBg: "bg-status-caution/20" },
  warning: { fill: "bg-status-warning", text: "text-status-warning", iconBg: "bg-status-warning/10" },
  danger: { fill: "bg-status-danger", text: "text-status-danger", iconBg: "bg-status-danger/10" },
};

// Segment thresholds mirror v_customer_lifecycle (days since last order):
// active <30, cooling 30-60, at_risk 60-90, lapsing 90-180, lost >=180.
const SEGMENT_INFO: {
  key: "cooling" | "at_risk" | "lapsing";
  label: string;
  days: string;
  level: 1 | 2 | 3;
  tone: Tone;
  Icon: LucideIcon;
  meaning: string;
}[] = [
  {
    key: "cooling",
    label: "Cooling",
    days: "30–60 days",
    level: 1,
    tone: "caution",
    Icon: Snowflake,
    meaning: "Their ordering habit is starting to slip. A friendly “we miss you” text is usually enough.",
  },
  {
    key: "at_risk",
    label: "At-risk",
    days: "60–90 days",
    level: 2,
    tone: "warning",
    Icon: AlertTriangle,
    meaning: "They're close to forgetting you: send a real offer. This is the sweet spot for win-backs.",
  },
  {
    key: "lapsing",
    label: "Lapsing",
    days: "90–180 days",
    level: 3,
    tone: "danger",
    Icon: HeartPulse,
    meaning: "Last chance before they're gone for good. Lead with your strongest offer and their favorite item.",
  },
];

type SegmentKey = (typeof SEGMENT_INFO)[number]["key"];

const segmentLabel = (key: string): string =>
  SEGMENT_INFO.find((s) => s.key === key)?.label ?? key;

// A 3-cell ramp that fills 1/2/3 cells by severity. Meaning survives grayscale:
// the number of filled cells, not their hue, is what carries the escalation.
const SeverityRamp = ({ level, fill }: { level: 1 | 2 | 3; fill: string }) => (
  <span className="flex items-center gap-0.5" role="img" aria-label={`Severity ${level} of 3`}>
    {[1, 2, 3].map((n) => (
      <span
        key={n}
        aria-hidden="true"
        className={`h-1.5 w-4 rounded-full ${n <= level ? fill : "bg-border"}`}
      />
    ))}
  </span>
);

const VALID_TABS = ["winback", "secondorder", "trend"] as const;
type TabKey = (typeof VALID_TABS)[number];

const VIP_SEGMENTS = ["loyal", "superfan"];

const RevenuePlays = () => {
  const { kpis, monthly, cohorts, winback, loading, error, refresh } = useRevenuePlays();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Promotions moved to /marketing (Campaign tab); keep old deep links alive.
  const tabParamRaw = searchParams.get("tab");
  useEffect(() => {
    if (tabParamRaw === "promotions") navigate("/marketing?tab=campaign", { replace: true });
  }, [tabParamRaw, navigate]);

  // Deep-linkable state: /revenue-plays?tab=secondorder, ?tab=winback&vip=1,
  // ?segment=lapsing — the Insights scorecard links straight to the data.
  const tabParam = searchParams.get("tab");
  const tab: TabKey = (VALID_TABS as readonly string[]).includes(tabParam ?? "")
    ? (tabParam as TabKey)
    : "winback";
  const [segment, setSegment] = useState<"all" | SegmentKey>(() => {
    const s = searchParams.get("segment");
    return SEGMENT_INFO.some((i) => i.key === s) ? (s as SegmentKey) : "all";
  });
  const [vipOnly, setVipOnly] = useState(() => searchParams.get("vip") === "1");

  const setTab = (value: string) => {
    setSearchParams(
      (prev) => {
        prev.set("tab", value);
        return prev;
      },
      { replace: true }
    );
  };

  const filteredWinback = useMemo(
    () =>
      winback
        .filter((w) => segment === "all" || w.recency_segment === segment)
        .filter((w) => !vipOnly || VIP_SEGMENTS.includes(w.frequency_segment)),
    [winback, segment, vipOnly]
  );

  const {
    sort: winbackSort,
    toggle: toggleWinbackSort,
    sorted: sortedWinback,
  } = useTableSort(filteredWinback, { key: "lifetime_spend", direction: "desc" });
  const displayedWinback = sortedWinback.slice(0, 150);

  const {
    sort: cohortSort,
    toggle: toggleCohortSort,
    sorted: sortedCohorts,
  } = useTableSort(cohorts, { key: "cohort_month", direction: "asc" });

  const {
    sort: monthlySort,
    toggle: toggleMonthlySort,
    sorted: sortedMonthly,
  } = useTableSort(monthly, { key: "month", direction: "asc" });

  const exportWinback = (rows: readonly WinbackTarget[], name: string) => {
    exportToCsv(
      stampedFilename(name),
      rows.map((r) => ({
        customer_name: r.customer_name ?? "",
        customer_phone: r.customer_phone,
        recency_segment: r.recency_segment,
        frequency_segment: r.frequency_segment,
        is_loyalty: r.is_loyalty,
        total_orders: r.total_orders,
        lifetime_spend: r.lifetime_spend,
        avg_order_value: r.avg_order_value,
        favorite_item: r.top_item ?? "",
        last_order_at: r.last_order_at,
        days_since_last_order: r.days_since_last_order,
      }))
    );
  };

  return (
    <div className="pb-10">
      {/* Header */}
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Revenue Plays</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Retention and win-back monitoring, built to grow repeat orders
          </p>
        </div>
        <Button
          onClick={refresh}
          variant="outline"
          disabled={loading}
          className="flex items-center gap-2 self-start"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-status-danger/30 bg-status-danger/10 px-4 py-3 text-sm text-status-danger">
          {error}
        </div>
      )}

      {/* KPI ladder. The middle three tiles are the recency escalation in the
          documented order: caution (cooling) -> warning (at-risk) -> danger
          (lapsing). One-time is the acquisition-side diagnostic (info), and the
          two green tiles are the upside (money on the table, VIP base). */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-8">
        <MetricCard
          icon={UserMinus}
          value={loading || !kpis ? "…" : `${kpis.one_time_pct}%`}
          label="Ordered only once"
          iconColor="text-status-info"
          iconBgColor="bg-status-info/10"
        />
        <MetricCard
          icon={Snowflake}
          value={loading || !kpis ? "…" : Number(kpis.cooling_customers)}
          label="Cooling (30–60d)"
          iconColor="text-status-caution-foreground"
          iconBgColor="bg-status-caution/20"
        />
        <MetricCard
          icon={AlertTriangle}
          value={loading || !kpis ? "…" : Number(kpis.at_risk_customers)}
          label="At-risk (60–90d)"
          iconColor="text-status-warning"
          iconBgColor="bg-status-warning/10"
        />
        <MetricCard
          icon={HeartPulse}
          value={loading || !kpis ? "…" : Number(kpis.lapsing_customers)}
          label="Lapsing (90–180d)"
          iconColor="text-status-danger"
          iconBgColor="bg-status-danger/10"
        />
        <MetricCard
          icon={TrendingUp}
          value={loading || !kpis ? "…" : money(kpis.winback_revenue_at_stake)}
          label="Revenue at stake"
          iconColor="text-brand-green"
          iconBgColor="bg-status-success/10"
        />
        <MetricCard
          icon={Crown}
          value={loading || !kpis ? "…" : kpis.superfans}
          label="Superfans (13+ orders)"
          iconColor="text-status-success"
          iconBgColor="bg-status-success/10"
        />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        {/* h-auto + flex-wrap: three long play labels must wrap, not overflow at 360px. */}
        <TabsList className="mb-4 h-auto flex-wrap">
          <TabsTrigger value="winback">Play #2 · Win-Back List</TabsTrigger>
          <TabsTrigger value="secondorder">Play #1 · 2nd-Order Funnel</TabsTrigger>
          <TabsTrigger value="trend">Revenue Trend</TabsTrigger>
        </TabsList>

        {/* ---------- WIN-BACK ---------- */}
        <TabsContent value="winback">
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="text-lg">Win-back targets</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Lapsed customers ranked by lifetime value, with their favorite item for
                  personalized SMS. {filteredWinback.length.toLocaleString()} customers in view.
                </p>
              </div>
              <Button
                onClick={() => exportWinback(sortedWinback, "winback-targets")}
                disabled={sortedWinback.length === 0}
                className="bg-brand-green hover:bg-brand-green/90 text-brand-white flex items-center gap-2 self-start shrink-0"
              >
                <Download className="w-4 h-4" />
                Export CSV ({filteredWinback.length.toLocaleString()})
              </Button>
            </CardHeader>
            <CardContent>
              {/* Segment legend: a readable escalation ramp. Meaning is carried by
                  icon + label + day-range + severity ramp + sentence, never by
                  color alone (WCAG: status is never color-only). */}
              <div className="mb-4">
                <div className="text-xs font-bold uppercase tracking-wide text-brand-gray mb-2">
                  How the win-back ladder works
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  {SEGMENT_INFO.map((s) => {
                    const tone = TONE[s.tone];
                    return (
                      <div key={s.key} className="rounded-lg border bg-muted p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`flex-none grid place-items-center w-7 h-7 rounded-md ${tone.iconBg}`}>
                            <s.Icon className={`w-4 h-4 ${tone.text}`} />
                          </span>
                          <Badge className={SEGMENT_STYLES[s.key]} variant="secondary">
                            {s.label}
                          </Badge>
                          <SeverityRamp level={s.level} fill={tone.fill} />
                        </div>
                        <div className="text-xs font-semibold text-muted-foreground mb-1">
                          {s.days} since last order
                        </div>
                        <p className="text-xs text-muted-foreground">{s.meaning}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Segment filter */}
              <div className="flex flex-wrap gap-2 mb-4">
                {(["all", ...SEGMENT_INFO.map((i) => i.key)] as const).map((s) => {
                  const info = SEGMENT_INFO.find((i) => i.key === s);
                  return (
                    <Button
                      key={s}
                      size="sm"
                      variant={segment === s ? "default" : "outline"}
                      onClick={() => setSegment(s)}
                      className={segment === s ? "bg-brand-red hover:bg-brand-red/90 text-brand-white" : ""}
                    >
                      {info ? `${info.label} (${info.days})` : "All"}
                    </Button>
                  );
                })}
                <Button
                  size="sm"
                  variant={vipOnly ? "default" : "outline"}
                  onClick={() => setVipOnly((v) => !v)}
                  className={vipOnly ? "bg-brand-green hover:bg-brand-green/90 text-brand-white" : ""}
                >
                  VIPs only (7+ orders)
                </Button>
              </div>

              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableHead label="Customer" sortKey="customer_name" sort={winbackSort} onToggle={toggleWinbackSort} />
                      <SortableHead label="Segment" sortKey="recency_segment" sort={winbackSort} onToggle={toggleWinbackSort} />
                      <SortableHead label="Orders" sortKey="total_orders" sort={winbackSort} onToggle={toggleWinbackSort} align="right" />
                      <SortableHead label="Lifetime $" sortKey="lifetime_spend" sort={winbackSort} onToggle={toggleWinbackSort} align="right" />
                      {/* Date column hides below sm; the date folds into the Customer cell there so a 360px table never gains a column. */}
                      <SortableHead label="Last ordered" sortKey="last_order_at" sort={winbackSort} onToggle={toggleWinbackSort} className="hidden sm:table-cell" />
                      <SortableHead label="Days idle" sortKey="days_since_last_order" sort={winbackSort} onToggle={toggleWinbackSort} align="right" />
                      <SortableHead label="Favorite item" sortKey="top_item" sort={winbackSort} onToggle={toggleWinbackSort} />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          Loading…
                        </TableCell>
                      </TableRow>
                    ) : displayedWinback.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          No customers in this segment.
                        </TableCell>
                      </TableRow>
                    ) : (
                      displayedWinback.map((w) => (
                        <TableRow key={w.customer_phone}>
                          <TableCell>
                            <div className="font-medium">{w.customer_name || "—"}</div>
                            <div className="text-xs text-muted-foreground">{w.customer_phone}</div>
                            {/* Mobile fold: the Last ordered date rides under the name below sm. */}
                            <div className="text-xs text-muted-foreground sm:hidden">
                              Last ordered {orderedDate(w.last_order_at)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={SEGMENT_STYLES[w.recency_segment] || ""} variant="secondary">
                              {segmentLabel(w.recency_segment)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{w.total_orders}</TableCell>
                          <TableCell className="text-right tabular-nums font-medium">
                            {money(w.lifetime_spend)}
                          </TableCell>
                          <TableCell className="hidden sm:table-cell tabular-nums whitespace-nowrap">
                            {orderedDate(w.last_order_at)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{w.days_since_last_order}</TableCell>
                          <TableCell className="text-sm">{w.top_item || "—"}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              {filteredWinback.length > displayedWinback.length && (
                <p className="text-xs text-muted-foreground mt-3">
                  Showing the first {displayedWinback.length} rows of the current sort. Use{" "}
                  <strong>Export CSV</strong> to download all {filteredWinback.length.toLocaleString()}.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------- SECOND ORDER ---------- */}
        <TabsContent value="secondorder">
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="text-lg">Second-order conversion by cohort</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Of customers who placed their first order in each month, what share came back for a
                  2nd within 30 days. This is the north-star metric for Play #1.
                </p>
              </div>
              <Button
                onClick={() => exportToCsv(stampedFilename("second-order-cohort"), sortedCohorts)}
                disabled={sortedCohorts.length === 0}
                className="bg-brand-green hover:bg-brand-green/90 text-brand-white flex items-center gap-2 self-start shrink-0"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableHead label="Cohort month" sortKey="cohort_month" sort={cohortSort} onToggle={toggleCohortSort} />
                      <SortableHead label="New customers" sortKey="new_customers" sort={cohortSort} onToggle={toggleCohortSort} align="right" />
                      <SortableHead label="2nd ≤ 30d" sortKey="second_within_30d" sort={cohortSort} onToggle={toggleCohortSort} align="right" />
                      <SortableHead label="2nd ≤ 60d" sortKey="second_within_60d" sort={cohortSort} onToggle={toggleCohortSort} align="right" />
                      <SortableHead label="% 2nd ≤ 30d" sortKey="pct_second_30d" sort={cohortSort} onToggle={toggleCohortSort} align="right" />
                      <SortableHead label="Median days to 2nd" sortKey="median_days_to_second" sort={cohortSort} onToggle={toggleCohortSort} align="right" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          Loading…
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedCohorts.map((c) => (
                        <TableRow key={c.cohort_month}>
                          <TableCell className="font-medium">{monthLabel(c.cohort_month)}</TableCell>
                          <TableCell className="text-right tabular-nums">{c.new_customers}</TableCell>
                          <TableCell className="text-right tabular-nums">{c.second_within_30d}</TableCell>
                          <TableCell className="text-right tabular-nums">{c.second_within_60d}</TableCell>
                          <TableCell className="text-right tabular-nums font-semibold text-brand-green">
                            {pct(c.pct_second_30d)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {c.median_days_to_second == null ? "—" : `${c.median_days_to_second}d`}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Industry benchmark: a healthy automated 2nd-order program lifts this to 25–40%. Recent
                months read low until their 30-day window matures.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------- REVENUE TREND ---------- */}
        <TabsContent value="trend">
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="text-lg">Monthly revenue, new vs returning</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Watch the new-customer column: acquisition is the leak to refill.
                </p>
              </div>
              <Button
                onClick={() => exportToCsv(stampedFilename("revenue-monthly"), sortedMonthly)}
                disabled={sortedMonthly.length === 0}
                className="bg-brand-green hover:bg-brand-green/90 text-brand-white flex items-center gap-2 self-start shrink-0"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableHead label="Month" sortKey="month" sort={monthlySort} onToggle={toggleMonthlySort} />
                      <SortableHead label="Orders" sortKey="orders" sort={monthlySort} onToggle={toggleMonthlySort} align="right" />
                      <SortableHead label="Customers" sortKey="customers" sort={monthlySort} onToggle={toggleMonthlySort} align="right" />
                      <SortableHead label="New" sortKey="new_customers" sort={monthlySort} onToggle={toggleMonthlySort} align="right" />
                      <SortableHead label="Returning" sortKey="returning_customers" sort={monthlySort} onToggle={toggleMonthlySort} align="right" />
                      <SortableHead label="Revenue" sortKey="revenue" sort={monthlySort} onToggle={toggleMonthlySort} align="right" />
                      <SortableHead label="Avg order" sortKey="avg_order_value" sort={monthlySort} onToggle={toggleMonthlySort} align="right" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          Loading…
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedMonthly.map((m) => (
                        <TableRow key={m.month}>
                          <TableCell className="font-medium">{monthLabel(m.month)}</TableCell>
                          <TableCell className="text-right tabular-nums">{m.orders}</TableCell>
                          <TableCell className="text-right tabular-nums">{m.customers}</TableCell>
                          <TableCell className="text-right tabular-nums text-brand-red font-medium">
                            {m.new_customers}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{m.returning_customers}</TableCell>
                          <TableCell className="text-right tabular-nums font-medium">
                            {money(m.revenue)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">${Number(m.avg_order_value).toFixed(2)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RevenuePlays;
